import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, Outlet, useLocation } from "react-router-dom";
import { LanguageProvider, useLanguage } from "./context/LanguageContext";
import ScrollToTop from "./components/ScrollToTop";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import CustomCursor from "./components/CustomCursor";
import FloatingWhatsAppButton from "./components/FloatingWhatsAppButton";

// Pages
import Home from "./pages/Home";
import About from "./pages/About";
import Specifications from "./pages/Specifications";
import InternationalSourcing from "./pages/InternationalSourcing";
import ForYarnManufacturers from "./pages/ForYarnManufacturers";
import ForInternationalSuppliers from "./pages/ForInternationalSuppliers";
import Partners from "./pages/Partners";
import Contact from "./pages/Contact";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsConditions from "./pages/TermsConditions";

// Dedicated B2B SEO Landing Pages
import WoolSupplierBikaner from "./pages/WoolSupplierBikaner";
import RawWoolSupplierIndia from "./pages/RawWoolSupplierIndia";
import CarpetWoolSupplierIndia from "./pages/CarpetWoolSupplierIndia";
import WoolAgentBikaner from "./pages/WoolAgentBikaner";
import BulkWoolSupply from "./pages/BulkWoolSupply";
import WoolSupplyRajasthan from "./pages/WoolSupplyRajasthan";

// Layout component to synchronize route parameters with LanguageContext
const LocalizedLayout = () => {
  const { lang } = useParams();
  const { changeLanguage } = useLanguage();
  const location = useLocation();

  const normalizedLang = lang ? lang.toLowerCase() : "en";
  const supportedLangs = ["en", "en-au", "en-nz", "ar", "zh-cn"];
  const isSupported = supportedLangs.includes(normalizedLang);

  useEffect(() => {
    if (isSupported) {
      changeLanguage(normalizedLang);
    }
  }, [normalizedLang, isSupported, changeLanguage]);

  if (!isSupported) {
    // Redirect to default "en" language but preserve the sub-route path
    const pathParts = location.pathname.split("/").filter(Boolean);
    const subPath = pathParts.slice(1).join("/");
    const suffix = subPath ? `/${subPath}` : "";
    return <Navigate to={`/en${suffix}${location.search}${location.hash}`} replace />;
  }

  return <Outlet />;
};

// Root redirection fallback for URLs without a language prefix
const RootRedirect = () => {
  const location = useLocation();
  const savedLang = localStorage.getItem("sk_woollens_lang") || "en";
  const supportedLangs = ["en", "en-au", "en-nz", "ar", "zh-cn"];
  const targetLang = supportedLangs.includes(savedLang.toLowerCase()) ? savedLang.toLowerCase() : "en";

  const pathParts = location.pathname.split("/").filter(Boolean);

  // Guard: if first segment is already a supported language, redirect directly to preserve it
  if (pathParts.length > 0 && supportedLangs.includes(pathParts[0].toLowerCase())) {
    return <Navigate to={location.pathname} replace />;
  }

  const suffix = pathParts.length > 0 ? `/${pathParts.join("/")}` : "";
  return <Navigate to={`/${targetLang}${suffix}${location.search}${location.hash}`} replace />;
};

function App() {
  return (
    <LanguageProvider>
      <Router>
        <ScrollToTop />
        <CustomCursor />
        <div className="min-h-screen flex flex-col bg-wool-bg text-wool-charcoal antialiased">
          <Navbar />
          {/* Main Content Area */}
          <main className="flex-1 pt-24 pb-12">
            <Routes>
              {/* Localized Routing */}
              <Route path="/:lang" element={<LocalizedLayout />}>
                <Route index element={<Home />} />
                <Route path="about" element={<About />} />
                <Route path="wool-specifications" element={<Specifications />} />
                <Route path="international-sourcing" element={<InternationalSourcing />} />
                <Route path="for-yarn-manufacturers" element={<ForYarnManufacturers />} />
                <Route path="for-international-suppliers" element={<ForInternationalSuppliers />} />
                <Route path="partners" element={<Partners />} />
                <Route path="contact" element={<Contact />} />
                <Route path="privacy-policy" element={<PrivacyPolicy />} />
                <Route path="terms" element={<TermsConditions />} />

                {/* Dedicated B2B SEO Landing Pages */}
                <Route path="wool-supplier-bikaner" element={<WoolSupplierBikaner />} />
                <Route path="raw-wool-supplier-india" element={<RawWoolSupplierIndia />} />
                <Route path="carpet-wool-supplier-india" element={<CarpetWoolSupplierIndia />} />
                <Route path="wool-agent-bikaner" element={<WoolAgentBikaner />} />
                <Route path="bulk-wool-supply" element={<BulkWoolSupply />} />
                <Route path="wool-supply-rajasthan" element={<WoolSupplyRajasthan />} />

                {/* Helpful Aliases & Alternative URL Slugs */}
                <Route path="about-us" element={<About />} />
                <Route path="wool-trading-bikaner" element={<WoolSupplierBikaner />} />
                <Route path="raw-wool-supplier" element={<RawWoolSupplierIndia />} />
                <Route path="carpet-grade-wool" element={<CarpetWoolSupplierIndia />} />
                <Route path="bulk-wool-trading" element={<BulkWoolSupply />} />
                <Route path="wool-for-yarn-manufacturers" element={<ForYarnManufacturers />} />
                <Route path="international-wool-sourcing" element={<InternationalSourcing />} />

                {/* Fallback internal redirect */}
                <Route path="*" element={<Navigate to="" replace />} />
              </Route>
              {/* Redirection for un-prefixed paths */}
              <Route path="*" element={<RootRedirect />} />
            </Routes>
          </main>
          <Footer />
          <FloatingWhatsAppButton />
        </div>
      </Router>
    </LanguageProvider>
  );
}

export default App;
